/**
 * CONFIGURACIÓN CENTRAL DEL INSTITUTO FREDERICK CHARLES COPLESTON
 * Edita este archivo para modificar textos, banderas de publicación (flags),
 * enlaces, fechas, lecturas y datos de contacto sin tocar los componentes.
 */

export interface HeroConfig {
  variant: "A" | "B" | "C";
  variants: {
    A: string;
    B: string;
    C: string;
  };
  smallHeader: string;
  subtitle: string;
  primaryButtonText: string;
  primaryButtonLink: string;
  secondaryButtonText: string;
  secondaryButtonLink: string;
}

export interface QuoteItem {
  id: string;
  cita: string;
  autor: string;
  obra: string;
  epoca: string;
}

export interface CirculoConfig {
  estado: string;
  frecuencia: string;
  diaHora: string;
  lugarCusco: string;
  enlaceVirtual: string;
  whatsappGroupUrl: string;
  whatsappShareText: string;
  primeraLectura: {
    titulo: string;
    autor: string;
    descripcion: string;
    enlaceDominioPublico: string;
  };
  rutaCiclo: Array<{
    fase: string;
    titulo: string;
    obras: string;
    descripcion: string;
  }>;
  faqs: Array<{
    pregunta: string;
    respuesta: string;
  }>;
}

export interface EncuestaOpcion {
  id: string;
  label: string;
}

export interface EscuelaConfig {
  heroTitle: string;
  heroSubtitle: string;
  compromiso: string;
  opcionesEncuesta: EncuestaOpcion[];
}

export interface ProximamenteSeccion {
  titulo: string;
  descripcion: string;
  temas: string[];
  enlace: string;
  publicado: boolean;
}

export const siteConfig = {
  // Información General
  nombre: "Instituto Frederick Charles Copleston",
  nombreCorto: "Instituto Copleston",
  lema: "Veritas et Sapientia",
  fraseEntrada: "Sapere Aude",
  ubicacion: "Cusco, Perú",
  correoContacto: "contacto@coplestoninstitute.com",
  telefonoWhatsApp: "+51984000000", // Placeholder editable por el usuario

  // Enlaces de Redes Sociales
  redes: {
    youtube: "https://www.youtube.com/@coplestoninstitute",
    instagram: "https://www.instagram.com/coplestoninstitute",
    tiktok: "https://www.tiktok.com/@coplestoninstitute",
    linkedin: "https://www.linkedin.com/company/coplestoninstitute",
  },

  // Banderas Globales (Feature Flags)
  flags: {
    loginActivo: false,         // Oculta login, modal de auth y botón /perfil
    campanaActiva: false,       // Oculta sección electoral UNSAAC (plancha, cargos, PDF)
    canalPublicado: false,      // true: muestra videos reales; false: diseño 'Próximamente'
    audiolibrosPublicado: false,// true: muestra audiolibros; false: diseño 'Próximamente'
    podcastPublicado: false,    // true: muestra episodios; false: diseño 'Próximamente'
    ensayosPublicado: false,    // true: muestra catálogo de artículos; false: 'Próximamente'
    rutasPublicado: false,      // true: muestra módulos interactivos; false: 'Próximamente'
  },

  // Datos del Fundador
  fundador: {
    nombre: "Edu Huallparimachi Huahuaccapa",
    titulo: "Fundador & Director Académico",
    foto: "/IMG/fundador-placeholder.webp", // Placeholder
    bioCorta:
      "Soy Edu Huallparimachi, cusqueño. Egresé de Psicología, estudio Derecho y me preparo para estudiar Filosofía en la UNSAAC. Creé el Instituto Copleston con una convicción simple: pensar con rigor no debería ser un privilegio. Por eso todo lo que hagamos aquí será libre y gratuito.",
    bioAmpliada:
      "Nacido en el Cusco, Edu Huallparimachi Huahuaccapa concibió el Instituto Copleston como una trinchera intelectual y formativa en los Andes. Tras graduarse en Psicología y profundizar en el estudio del Derecho en la Universidad Continental, orientó su vocación hacia el rescate de la filosofía clásica, postulando a la Escuela Profesional de Filosofía de la Universidad Nacional de San Antonio Abad del Cusco (UNSAAC). Su visión rechaza el mercantilismo académico contemporáneo y defiende la divulgación abierta, rigurosa y sin coste de las grandes obras de la humanidad.",
  },

  // In Memoriam
  inMemoriam: {
    nombre: "Frederick Charles Copleston, S.J.",
    anios: "1907 – 1994",
    semblanza:
      "Sacerdote jesuita y filósofo británico, Frederick C. Copleston fue uno de los más insignes historiadores de la filosofía del siglo XX. Su monumental obra 'Historia de la Filosofía' (en nueve volúmenes) destaca por su claridad pedagógica, imparcialidad intelectual y respeto meticuloso por el pensamiento de cada autor, desde los presocráticos hasta el siglo XX. El Instituto toma su nombre y su método como modelo permanente.",
    obraPrincipal: "Historia de la Filosofía (9 Tomos, 1946–1975)",
  },

  // Configuración del Hero (Inicio)
  hero: {
    variant: "A",
    variants: {
      A: "Pensar con rigor, libre y gratuito, desde Cusco.",
      B: "La filosofía es para todos. Pensemos juntos.",
      C: "Filosofía y psicología con rigor, en español y sin costo.",
    },
    smallHeader: "SAPERE AUDE · CUSCO",
    subtitle:
      "Un espacio independiente para estudiar filosofía, psicología y pensamiento estratégico: lecturas, debates, videos y audiolibros de los clásicos.",
    primaryButtonText: "Únete al Círculo de Estudio",
    primaryButtonLink: "/circulo",
    secondaryButtonText: "Sigue el canal",
    secondaryButtonLink: "https://www.youtube.com/@coplestoninstitute",
  } as HeroConfig,

  // Por Qué Copleston (Franja institucional)
  porQueCopleston: {
    titulo: "¿Por qué Copleston?",
    texto:
      "Frederick C. Copleston (1907–1994) escribió una de las historias de la filosofía más claras y honestas del siglo XX. Tomamos su nombre porque compartimos su método: explicar a los grandes pensadores con fidelidad, claridad y respeto por el lector.",
  },

  // Citas Filosóficas Rotativas (Dominio Público)
  citas: [
    {
      id: "kant",
      cita: "Sapere aude! ¡Ten el valor de servirte de tu propio entendimiento! He aquí el lema de la Ilustración.",
      autor: "Immanuel Kant",
      obra: "¿Qué es la Ilustración?",
      epoca: "1784",
    },
    {
      id: "platon",
      cita: "Una vida sin examen no merece la pena ser vivida por el ser humano.",
      autor: "Platón",
      obra: "Apología de Sócrates",
      epoca: "c. 399 a.C.",
    },
    {
      id: "aristoteles",
      cita: "Todos los hombres por naturaleza desean saber.",
      autor: "Aristóteles",
      obra: "Metafísica, Libro I",
      epoca: "c. 350 a.C.",
    },
    {
      id: "spinoza",
      cita: "No burlarse, no lamentarse, ni detestar las acciones humanas, sino comprenderlas.",
      autor: "Baruch Spinoza",
      obra: "Tratado Político",
      epoca: "1677",
    },
    {
      id: "seneca",
      cita: "No es que tengamos poco tiempo, sino que perdemos mucho.",
      autor: "Séneca",
      obra: "De la brevedad de la vida",
      epoca: "c. 49 d.C.",
    },
    {
      id: "pascal",
      cita: "El hombre no es más que una caña, la más débil de la naturaleza; pero es una caña pensante.",
      autor: "Blaise Pascal",
      obra: "Pensamientos",
      epoca: "1670",
    },
    {
      id: "agustin",
      cita: "Si me engaño, existo (Si enim fallor, sum).",
      autor: "San Agustín de Hipona",
      obra: "La Ciudad de Dios, Libro XI",
      epoca: "426 d.C.",
    },
  ] as QuoteItem[],

  // Círculo de Estudio
  circulo: {
    estado: "Convocatoria abierta · Primer ciclo",
    frecuencia: "Cada 15 días",
    diaHora: "Sábados, 5:00 p.m. (hora de Perú)",
    lugarCusco: "Centro Histórico de Cusco (sede rotativa comunicada al grupo)",
    enlaceVirtual: "Sesión sincrónica vía Google Meet para miembros a distancia",
    whatsappGroupUrl: "https://chat.whatsapp.com/CoplestonCirculo", // Reemplazar con enlace real
    whatsappShareText:
      "¡Hola! Te invito a unirte al Círculo de Estudio gratuito del Instituto Copleston en Cusco: leemos a los clásicos y debatimos con rigor filosófico. Inscríbete gratis aquí: https://coplestoninstitute.com/circulo",
    primeraLectura: {
      titulo: "Apología de Sócrates",
      autor: "Platón",
      descripcion:
        "El punto de partida insustituible. En este diálogo presenciamos el juicio y la defensa de Sócrates frente a la asamblea ateniense. Es el alegato fundacional sobre por qué la filosofía y el cuestionamiento del dogma son vitales para la polis.",
      enlaceDominioPublico: "https://es.wikisource.org/wiki/Apolog%C3%ADa_de_S%C3%B3crates_(Plat%C3%B3n)",
    },
    rutaCiclo: [
      {
        fase: "I",
        titulo: "El Nacimiento del Logos",
        obras: "Presocráticos: De Tales a Demócrito",
        descripcion: "El tránsito del mito a la razón y la búsqueda del principio de la physis.",
      },
      {
        fase: "II",
        titulo: "El Giro Antropológico",
        obras: "Sócrates y los Sofistas (Protágoras, Gorgias)",
        descripcion: "El examen moral del ciudadano, la retórica del poder y la mayéutica.",
      },
      {
        fase: "III",
        titulo: "El Idealismo Platónico",
        obras: "Platón: República (Libros VI-VII) y Fedón",
        descripcion: "La alegoría de la caverna, la teoría de las ideas y la inmortalidad del alma.",
      },
      {
        fase: "IV",
        titulo: "El Realismo y la Ciencia",
        obras: "Aristóteles: Ética a Nicómaco y Metafísica",
        descripcion: "La teleología, la virtud como término medio y la estructura de la sustancia.",
      },
    ],
    faqs: [
      {
        pregunta: "¿Tiene algún costo o mensualidad?",
        respuesta:
          "Ninguno. El Instituto Copleston y el Círculo de Estudio son 100% gratuitos. Creemos firmemente que el acceso a la filosofía debe ser libre.",
      },
      {
        pregunta: "¿Necesito conocimientos previos de filosofía?",
        respuesta:
          "No. El único requisito es leer el texto asignado antes de cada sesión y participar con respeto, honestidad y deseo genuino de aprender.",
      },
      {
        pregunta: "¿Qué pasa si no puedo asistir a todas las sesiones?",
        respuesta:
          "Entendemos las obligaciones académicas y laborales. Puedes reincorporarte en cualquier lectura; compartimos guías previas por el grupo de WhatsApp.",
      },
      {
        pregunta: "¿Dónde y cuándo se reúnen?",
        respuesta:
          "Nos reunimos quincenalmente los sábados a las 5:00 p.m., alternando sesiones presenciales en Cusco con conexión sincrónica virtual para quienes residen fuera.",
      },
      {
        pregunta: "¿Quién organiza y modera los debates?",
        respuesta:
          "Es coordinado por Edu Huallparimachi y estudiantes colaboradores de la Escuela de Filosofía de la UNSAAC, facilitando el debate sin jerarquías dogmáticas.",
      },
    ],
  } as CirculoConfig,

  // Escuela de Filosofía UNSAAC
  escuelaFilosofia: {
    heroTitle: "Escuela de Filosofía UNSAAC",
    heroSubtitle: "Un espacio para pensar juntos cómo mejorar nuestra Escuela.",
    compromiso:
      "Creo que una Escuela de Filosofía debe ser el lugar donde más se lee, se debate y se escribe en toda la universidad. Este espacio existe para escuchar a los estudiantes y convertir sus ideas en propuestas concretas.",
    opcionesEncuesta: [
      { id: "libros", label: "Acceso a libros, biblioteca física y repositorios académicos" },
      { id: "eventos", label: "Eventos, conferencias magistrales y coloquios con ponentes invitados" },
      { id: "investigacion", label: "Investigación, semilleros y asesoría de tesis metodológica" },
      { id: "publicaciones", label: "Publicaciones estudiantiles, revistas indexadas y ensayos" },
      { id: "docentes", label: "Actualización de sílabos, rigor docente y evaluación pedagógica" },
      { id: "bienestar", label: "Bienestar, salud mental y espacios dignos de estudio" },
    ],
  } as EscuelaConfig,

  // Lo que viene (Tarjetas de Próximamente)
  loQueViene: {
    canal: {
      titulo: "Canal de YouTube",
      descripcion:
        "Historia de la Filosofía, autor por autor. Serie basada en la obra de Frederick Copleston, más videos de psicología.",
      temas: [
        "¿Por qué Copleston? La historia más honesta de la filosofía",
        "Los presocráticos: el nacimiento del logos",
        "¿La conducta es lo único que existe? Psicología vs. Filosofía",
        "Hegel y la Fenomenología del espíritu",
      ],
      enlace: "/canal",
      publicado: false,
    },
    audiolibros: {
      titulo: "Audiolibros Clásicos",
      descripcion:
        "Los clásicos, leídos en voz alta. Obras de filosofía e historia de la psicología de dominio público, leídas en cámara y por capítulos.",
      temas: [
        "Platón: Apología de Sócrates (Completo)",
        "Séneca: De la brevedad de la vida",
        "Descartes: Discurso del Método",
        "Nietzsche: El crepúsculo de los ídolos",
      ],
      enlace: "/audiolibros",
      publicado: false,
    },
    podcast: {
      titulo: "Logos: El Podcast",
      descripcion:
        "Conversaciones sobre filosofía, poder, psicología y mente. Diálogos pausados con notas y bibliografía rigurosa.",
      temas: [
        "Episodio 01: ¿Qué es pensar estratégicamente?",
        "Episodio 02: Maquiavelo y la anatomía de la dominación",
        "Episodio 03: Cognición, sesgos y autoengaño",
        "Episodio 04: Filosofía andina y pensamiento universal",
      ],
      enlace: "/podcast",
      publicado: false,
    },
    ensayos: {
      titulo: "Ensayos & Artículos",
      descripcion:
        "Ensayos breves de filosofía y psicología redactados con rigor metodológico, aparato crítico y bibliografía verificable.",
      temas: [
        "El problema del método en Frederick Copleston",
        "La paradoja de la voluntad en Schopenhauer",
        "Bases cognitivas de la polarización política",
        "Rigor conceptual vs. Charlatanería moderna",
      ],
      enlace: "/ensayos",
      publicado: false,
    },
    rutas: {
      titulo: "Rutas de Estudio Autoguiadas",
      descripcion:
        "Itinerarios formativos gratuitos para estudiar por cuenta propia con lecturas guiadas y análisis conceptual.",
      temas: [
        "Ruta I: Filosofía del Poder y Estrategia",
        "Ruta II: Arquitectura de la Mente y Psicología Cognitiva",
        "Ruta III: Historiología y Filosofía de la Historia",
        "Ruta IV: Lógica y Análisis Argumentativo",
      ],
      enlace: "/rutas",
      publicado: false,
    },
  },

  // Newsletter
  newsletter: {
    titulo: "No te pierdas nada",
    descripcion:
      "Te avisamos cuando publiquemos cada video, audiolibro, episodio o ensayo. Sin spam, solo filosofía.",
    consentimientoTexto:
      "Acepto la política de privacidad y autorizo el tratamiento de mis datos personales conforme a la Ley N.° 29733 de Protección de Datos Personales del Perú.",
  },

  // Menú Principal de Navegación
  menu: [
    { nombre: "Inicio", href: "/" },
    { nombre: "Círculo de Estudio", href: "/circulo" },
    { nombre: "Escuela de Filosofía", href: "/escuela-filosofia" },
    { nombre: "Canal", href: "/canal" },
    { nombre: "Audiolibros", href: "/audiolibros" },
    { nombre: "Podcast", href: "/podcast" },
    { nombre: "Ensayos", href: "/ensayos" },
    { nombre: "Nosotros", href: "/nosotros" },
  ],
};
